import { LocalizedText } from "@/common/common";
import { RoleType } from "@/types/enums/RoleType";
import type { TemplateContent } from "@/templates/templateDomain";
import { AccountRoleType } from "@/companyRoles/accountRoleTypeApiClient";
import type {
  CompanyCustomField,
  CompanySystemField,
  CustomFieldValue,
  SystemFieldKey,
} from "@/customFields/customFieldsDomain";
import {
  HttpEvent,
  HttpEventCallbacks,
  httpDelete,
  httpGet,
  httpPost,
  httpPut,
} from "@/events/eventsDomain";

export type DashboardStatus = "ENABLED" | "DISABLED";

export type DashboardCardType = "ENTITY_DATA" | "COMPANY_ROLES" | "ROLE_HOLDER" | "TEMPLATE";

export type DashboardGroupType = "ACCOUNT_ADMINS" | "COMPANY_ADMINS" | "CUSTOM_ROLE";

export type FieldRef<K extends string> =
  | { source: "SYSTEM"; field: K }
  | { source: "CUSTOM"; definitionId: string };

export type EntityDataField = FieldRef<SystemFieldKey>;

export type CardRole =
  | { source: "SYSTEM"; role: RoleType }
  | { source: "ACCOUNT"; roleTypeId: string };

export const personFields = [
  "NAME",
  "EMAIL",
  "PHONE_NUMBER",
  "NATIONALITY",
  "FROM",
  "TO",
  "RESIDENTIAL_COUNTRY",
  "RESIDENTIAL_ADDRESS_LINE1",
  "RESIDENTIAL_POSTAL_CODE",
  "RESIDENTIAL_CITY",
] as const;

export type PersonField = (typeof personFields)[number];

export type RoleHolderField = FieldRef<PersonField>;

export type DashboardCardConfig =
  | { type: "ENTITY_DATA"; fields: EntityDataField[] }
  | { type: "COMPANY_ROLES"; roles: CardRole[] }
  | { type: "ROLE_HOLDER"; role: CardRole; fields: RoleHolderField[] }
  | { type: "TEMPLATE"; templateId: string };

export type Dashboard = {
  id: string;
  internalName: string;
  title: LocalizedText[];
  heading?: LocalizedText[];
  hasDatePicker: boolean;
  status: DashboardStatus;
  cardIds: string[];
  visibleTo: string[];
  updatedAt: string;
};

export type DashboardCardDefinition = {
  id: string;
  internalName: string;
  heading: LocalizedText[];
  type: DashboardCardType;
  config: DashboardCardConfig;
  dashboardCount: number;
  createdAt: string;
};

export type DashboardGroup = {
  grantee: string;
  type: DashboardGroupType;
  name?: string;
  memberCount: number;
};

export type DashboardOverview = {
  systemDefaultStatus: DashboardStatus;
  dashboards: Dashboard[];
  cards: DashboardCardDefinition[];
  groups: DashboardGroup[];
};

export type CompanyDashboards = {
  isSystemDefaultEnabled: boolean;
  dashboards: CompanyDashboard[];
};

export type DashboardRequest = {
  internalName: string;
  title: LocalizedText[];
  heading?: LocalizedText[];
  hasDatePicker: boolean;
  status: DashboardStatus;
  cardIds: string[];
  visibleTo: string[];
};

export type DashboardCardDefinitionRequest = {
  internalName: string;
  heading: LocalizedText[];
  config: DashboardCardConfig;
};

export type EntityDataRow =
  | { source: "SYSTEM"; field: CompanySystemField }
  | { source: "CUSTOM"; field: CompanyCustomField };

export type RoleRow = {
  role: RoleType;
  accountRoleType?: AccountRoleType;
  name: string;
};

export type RoleHolderRow =
  | { source: "SYSTEM"; field: PersonField; value?: CustomFieldValue }
  | { source: "CUSTOM"; field: CompanyCustomField };

export type RoleHolderBlock = { name: string; rows: RoleHolderRow[] };

export type DashboardCardData =
  | { type: "ENTITY_DATA"; rows: EntityDataRow[] }
  | { type: "COMPANY_ROLES"; rows: RoleRow[] }
  | { type: "ROLE_HOLDER"; holders: RoleHolderBlock[] }
  | { type: "TEMPLATE"; content: TemplateContent };

export type DashboardCardDataRequest = {
  cards: { key: string; config: DashboardCardConfig }[];
  asOf?: string;
  previewAs?: string;
};

export type DashboardCardDataResponse = { cards: Record<string, DashboardCardData> };

export type CompanyDashboardCard = {
  id: string;
  heading: LocalizedText[];
  config: DashboardCardConfig;
};

export type CompanyDashboard = {
  id: string;
  title: LocalizedText[];
  heading?: LocalizedText[];
  hasDatePicker: boolean;
  cards: CompanyDashboardCard[];
};

export type DashboardApiClient = {
  companyDashboards: (
    orgNo: string,
    params: HttpEventCallbacks<CompanyDashboards>,
  ) => HttpEvent<CompanyDashboards>;
  cardData: (
    orgNo: string,
    request: DashboardCardDataRequest,
    params: HttpEventCallbacks<DashboardCardDataResponse>,
  ) => HttpEvent<DashboardCardDataResponse>;
  overview: (
    accountId: string,
    params: HttpEventCallbacks<DashboardOverview>,
  ) => HttpEvent<DashboardOverview>;
  addDashboard: (
    accountId: string,
    request: DashboardRequest,
    params: HttpEventCallbacks<DashboardOverview>,
  ) => HttpEvent<DashboardOverview>;
  updateDashboard: (
    accountId: string,
    dashboardId: string,
    request: DashboardRequest,
    params: HttpEventCallbacks<DashboardOverview>,
  ) => HttpEvent<DashboardOverview>;
  deleteDashboard: (
    accountId: string,
    dashboardId: string,
    params: HttpEventCallbacks<DashboardOverview>,
  ) => HttpEvent<DashboardOverview>;
  setSystemDefaultStatus: (
    accountId: string,
    status: DashboardStatus,
    params: HttpEventCallbacks<DashboardOverview>,
  ) => HttpEvent<DashboardOverview>;
  addCard: (
    accountId: string,
    request: DashboardCardDefinitionRequest,
    params: HttpEventCallbacks<DashboardOverview>,
  ) => HttpEvent<DashboardOverview>;
  updateCard: (
    accountId: string,
    cardId: string,
    request: DashboardCardDefinitionRequest,
    params: HttpEventCallbacks<DashboardOverview>,
  ) => HttpEvent<DashboardOverview>;
  deleteCard: (
    accountId: string,
    cardId: string,
    params: HttpEventCallbacks<DashboardOverview>,
  ) => HttpEvent<DashboardOverview>;
};

const accountUrl = (accountId: string) => `/enterprise-accounts/${encodeURIComponent(accountId)}`;

const dashboardsUrl = (accountId: string) => `${accountUrl(accountId)}/dashboards`;

const cardsUrl = (accountId: string) => `${accountUrl(accountId)}/dashboard-card-definitions`;

export const dashboardApiClient: DashboardApiClient = {
  companyDashboards: (orgNo, params) =>
    httpGet({ url: `/companies/${encodeURIComponent(orgNo)}/dashboards`, ...params }),
  cardData: (orgNo, request, params) =>
    httpPost({
      url: `/companies/${encodeURIComponent(orgNo)}/dashboard-card-data`,
      data: request,
      ...params,
    }),
  overview: (accountId, params) => httpGet({ url: dashboardsUrl(accountId), ...params }),
  addDashboard: (accountId, request, params) =>
    httpPost({ url: dashboardsUrl(accountId), data: request, ...params }),
  updateDashboard: (accountId, dashboardId, request, params) =>
    httpPut({
      url: `${dashboardsUrl(accountId)}/${encodeURIComponent(dashboardId)}`,
      data: request,
      ...params,
    }),
  deleteDashboard: (accountId, dashboardId, params) =>
    httpDelete({
      url: `${dashboardsUrl(accountId)}/${encodeURIComponent(dashboardId)}`,
      ...params,
    }),
  setSystemDefaultStatus: (accountId, status, params) =>
    httpPut({
      url: `${accountUrl(accountId)}/system-default-dashboard`,
      data: { status },
      ...params,
    }),
  addCard: (accountId, request, params) =>
    httpPost({ url: cardsUrl(accountId), data: request, ...params }),
  updateCard: (accountId, cardId, request, params) =>
    httpPut({
      url: `${cardsUrl(accountId)}/${encodeURIComponent(cardId)}`,
      data: request,
      ...params,
    }),
  deleteCard: (accountId, cardId, params) =>
    httpDelete({ url: `${cardsUrl(accountId)}/${encodeURIComponent(cardId)}`, ...params }),
};
